// Controla quais pedidos cada pessoa pode ver, conforme o tipo de documento de
// quem está logado (PF/PJ) e, pra PF, se é inscrita na OAB e em que categoria
// (Advogado(a) ou Estagiário(a) — vem de authService.getOABData()).
//
// Regras confirmadas com o time de negócio:
// - PJ (CNPJ): só vê os pedidos de categoria "sociedades" — e mesmo assim sem
//   os 2 de Registro de Sociedade (quem já é uma sociedade não registra outra) —
//   mais a Atualização Cadastral, sem os campos exclusivos de PF.
// - PF (CPF): nunca vê a categoria "sociedades", EXCETO os 2 de Registro de
//   Sociedade e as 3 Juntadas de Sociedade (Diligência/Reconsideração/Recurso)
//   — esses só aparecem se ela já for inscrita na OAB como
//   Advogado(a) (tipoCategoria "ADVOGADO(A)"; tanto a inscrição Originária
//   quanto a Suplementar levam a essa categoria, não existe uma distinção
//   separada entre as duas nos dados de login).
// - Fora da categoria Sociedade, cada pedido de PF cai em um destes grupos:
//   1. Sempre visível, inscrita ou não (ex: Juntada de Recurso/Diligência/
//      Reconsideração, que também servem pra quem ainda está com o pedido de
//      inscrição em andamento e precisa responder a um problema nele);
//   2. Só visível se já for inscrita, em QUALQUER categoria (Advogado(a) ou
//      Estagiário(a)) — pedidos administrativos genéricos que fazem sentido
//      pros dois (ex: cancelamento, alteração de nome, segunda via,
//      recadastramento).
//   3. Só visível se já for inscrita ESPECIFICAMENTE como Advogado(a) — atos que
//      só existem pra quem já tem inscrição plena (licenciamento, impedimento,
//      reativação, transformação de suplementar); Estagiário(a) não vê, mesmo
//      inscrita.
//   4. Inscrição Originária/Suplementar/Estagiário/Transferência têm regra
//      própria (ver isPedidoVisivelParaUsuario): Estagiário(a) só progride pela
//      Originária (única "porta de entrada" pra virar Advogado(a) que continua
//      visível pra quem já é Estagiário); Suplementar e Transferência somem
//      assim que a pessoa já está inscrita, seja como Estagiário(a) ou já
//      Advogado(a) (fazem sentido só pra quem ainda não tem nenhuma inscrição).
//   5. Prorrogação de Estágio é exclusiva de quem está inscrita como
//      Estagiário(a) agora — Advogado(a) já não tem estágio pra prorrogar,
//      mesmo estando inscrita.
import { formatCPFCNPJ } from '../../../../utils/formatters';

const REGISTRO_SOCIEDADE_IDS = ['pedido_registro_sociedade_unipessoal', 'pedido_registro_sociedade_pluripessoal'];

// Fora da categoria Sociedade, os únicos pedidos que PJ também vê. Campos que
// não fazem sentido pra sociedade (Etnia, Deficiência...) são escondidos pra ela
// por `apenasPF: true` no config — ver adaptarConfigAoPerfil.
const PJ_TAMBEM_VISIVEL = new Set(['pedido_atualizacao_cadastral']);

// Juntadas de Sociedade: além da própria sociedade (PJ), o(a) advogado(a) (PF
// inscrita como Advogado(a)) também pode protocolar em nome dela.
const JUNTADA_SOCIEDADE_IDS = [
  'pedido_juntada_diligencia_sociedade',
  'pedido_juntada_reconsideracao_sociedade',
  'pedido_juntada_recurso_sociedade'
];

const PF_SEMPRE_VISIVEL = new Set([
  'pedido_PrimeiraViaExameDeOrdem',
  'pedido_SegundaViaExameDeOrdem',
  'pedido_alteracao_nome_certificado_exame_ordem',
  // Juntadas: quem ainda não é inscrito também precisa delas, pra responder a
  // um problema no próprio pedido de inscrição (Originária, Estagiário etc.).
  'pedido_juntada_diligencia',
  'pedido_juntada_reconsideracao',
  'pedido_juntada_recurso',
  // Atualização Cadastral: qualquer PF, inscrita ou não na OAB.
  'pedido_atualizacao_cadastral',
  // Certidões de Inteiro Teor: qualquer PF, inscrita ou não na OAB.
  'pedido_certidao_inteiro_teor',
  'pedido_certidao_inteiro_teor_transferencia',
  'pedido_certidao_inteiro_teor_sociedade',
  'pedido_certidao_inteiro_teor_suplementar'
]);

// Visível pra qualquer PF já inscrita, Advogado(a) ou Estagiário(a) — pedidos
// administrativos genéricos, não exclusivos de uma categoria de inscrição.
const PF_SO_SE_INSCRITA = new Set([
  'pedido_solicitacao_segunda_via',
  'pedido_recadastramento',
  'pedido_cancelamento',
  'pedido_alteracao_nome',
  'pedido_desarquivamento_processo',
  'pedido_envio_termo_posse',
  // Atualização de Subseção: só PF já inscrita (Advogado(a) ou Estagiário(a)).
  'pedido_atualizacao_subsecao'
]);

// Visível só pra PF já inscrita ESPECIFICAMENTE como Advogado(a) — atos que não
// fazem sentido pra quem ainda é só Estagiário(a) (não tem inscrição plena pra
// licenciar, sofrer impedimento, reativar ou transformar).
const PF_SO_SE_ADVOGADO = new Set([
  'pedido_transformacao_suplementar_transferencia',
  'pedido_reativacao',
  'pedido_licenciamento',
  'pedido_baixa_Impedimento',
  'pedido_baixa_licenciamento',
  'pedido_anotacao_impedimento'
]);

// inscricao_estagiario, inscricao_originaria, inscricao_suplementar e
// inscricao_transferencia têm regra própria — ver isPedidoVisivelParaUsuario.

const ehCategoriaSociedade = (categoria) => {
  const categorias = Array.isArray(categoria) ? categoria : [categoria];
  return categorias.includes('sociedades');
};

/**
 * @param {Object} user - usuário logado (useAuth)
 * @returns {'PF'|'PJ'}
 */
export const getTipoInscricaoUsuario = (user) => {
  const digitos = String(user?.documento || '').replace(/\D/g, '');
  return digitos.length === 14 ? 'PJ' : 'PF';
};

/**
 * Monta o contexto de acesso a partir do usuário logado e dos dados de OAB
 * (authService.getOABData()) — pronto pra passar em isPedidoVisivelParaUsuario.
 */
export const getContextoAcesso = (user, oabData) => ({
  tipoInscricao: getTipoInscricaoUsuario(user),
  isInscrito: !!oabData?.isInscrito,
  tipoCategoria: oabData?.tipoCategoria
});

/**
 * Certidões Online (Certidão Nada Consta): só PF já inscrita como Advogado(a).
 * PJ, PF não inscrita e Estagiário(a) não têm acesso a essa certidão — o card
 * some da Home e a rota /certidoes-online mostra o motivo no lugar da certidão.
 *
 * @param {{tipoInscricao: 'PF'|'PJ', isInscrito: boolean, tipoCategoria?: string}} contexto
 */
export const podeAcessarCertidoesOnline = ({ tipoInscricao, isInscrito, tipoCategoria }) =>
  tipoInscricao === 'PF' && isInscrito && tipoCategoria === 'ADVOGADO(A)';

/**
 * @param {{id: string, categoria: string|string[]}} pedido - config do pedido ou
 *   entrada retornada por getPedidosByDepartamento (ambos têm id + categoria)
 * @param {{tipoInscricao: 'PF'|'PJ', isInscrito: boolean, tipoCategoria?: string}} contexto
 */
export const isPedidoVisivelParaUsuario = (pedido, { tipoInscricao, isInscrito, tipoCategoria }) => {
  const isSociedade = ehCategoriaSociedade(pedido.categoria);
  const isRegistroSociedade = REGISTRO_SOCIEDADE_IDS.includes(pedido.id);

  if (tipoInscricao === 'PJ') {
    return (isSociedade && !isRegistroSociedade) || PJ_TAMBEM_VISIVEL.has(pedido.id);
  }

  // A partir daqui, tipoInscricao === 'PF'
  if (isRegistroSociedade || JUNTADA_SOCIEDADE_IDS.includes(pedido.id)) {
    return isInscrito && tipoCategoria === 'ADVOGADO(A)';
  }

  if (isSociedade) {
    return false;
  }

  if (pedido.id === 'inscricao_estagiario') {
    return !isInscrito;
  }

  // Inscrição Originária é a única "porta de entrada" que continua visível pra
  // quem já é Estagiário(a) (progressão natural pra virar Advogado(a)); some só
  // quando ela já for Advogado(a).
  if (pedido.id === 'inscricao_originaria') {
    return !isInscrito || tipoCategoria === 'ESTAGIÁRIO(A)';
  }

  // Suplementar e Transferência só fazem sentido pra quem ainda não tem
  // nenhuma inscrição — somem assim que a pessoa já está inscrita, mesmo como
  // Estagiário(a) (diferente da Originária, essas duas não servem de
  // progressão de Estagiário pra Advogado).
  if (pedido.id === 'inscricao_suplementar' || pedido.id === 'inscricao_transferencia') {
    return !isInscrito;
  }

  // Certidão de Não Inscrito: só faz sentido pra quem não tem inscrição na OAB.
  if (pedido.id === 'pedido_certidao_nao_inscrito') {
    return !isInscrito;
  }

  // Prorrogação de Estágio só faz sentido pra quem está inscrita AGORA como
  // Estagiário(a) — quem já é Advogado(a) não tem estágio pra prorrogar.
  if (pedido.id === 'pedido_prorrogacao_estagio') {
    return isInscrito && tipoCategoria === 'ESTAGIÁRIO(A)';
  }

  // Inscrição para Comissões da OAB/MS: cada pedido é exclusivo de uma categoria
  // (a inadimplência não filtra a listagem — é bloqueada dentro do próprio pedido,
  // com mensagem explicando o motivo, ver DynamicPedidoPage.js).
  if (pedido.id === 'pedido_comissoes_advogado') {
    return isInscrito && tipoCategoria === 'ADVOGADO(A)';
  }

  if (pedido.id === 'pedido_comissoes_estagiario') {
    return isInscrito && tipoCategoria === 'ESTAGIÁRIO(A)';
  }

  if (PF_SEMPRE_VISIVEL.has(pedido.id)) {
    return true;
  }

  if (PF_SO_SE_ADVOGADO.has(pedido.id)) {
    return isInscrito && tipoCategoria === 'ADVOGADO(A)';
  }

  return PF_SO_SE_INSCRITA.has(pedido.id) && isInscrito;
};

// Os 4 perfis de login que existem na prática — usados por
// getMotivoBloqueioPedido pra descobrir quem PODE fazer um pedido.
const PERFIL_PJ = { tipoInscricao: 'PJ', isInscrito: false };
const PERFIL_PF_NAO_INSCRITA = { tipoInscricao: 'PF', isInscrito: false };
const PERFIL_PF_ADVOGADO = { tipoInscricao: 'PF', isInscrito: true, tipoCategoria: 'ADVOGADO(A)' };
const PERFIL_PF_ESTAGIARIO = { tipoInscricao: 'PF', isInscrito: true, tipoCategoria: 'ESTAGIÁRIO(A)' };

export const MOTIVOS_BLOQUEIO_PEDIDO = {
  SO_PJ: 'Este pedido não pode ser feito com login de pessoa física. Para solicitá-lo, saia e entre com o login da sociedade (pessoa jurídica), usando o CNPJ e a senha dela.',
  SO_PF: 'Este pedido não pode ser feito com login de sociedade (pessoa jurídica). Para solicitá-lo, saia e entre com o login da pessoa física, usando o CPF e a senha.',
  SO_ADVOGADO_PF: 'Este pedido não pode ser feito com login de sociedade (pessoa jurídica). Ele é feito pelo(a) advogado(a) inscrito(a) na OAB/MS: saia e entre com o login dele(a), usando o CPF e a senha.',
  SO_ADVOGADO_OU_SOCIEDADE: 'Este pedido só pode ser feito por advogado(a) inscrito(a) na OAB/MS ou pela própria sociedade, com o login dela (CNPJ e senha).',
  SO_ADVOGADO: 'Este pedido é exclusivo de advogado(a) inscrito(a) na OAB/MS.',
  SO_ESTAGIARIO: 'Este pedido é exclusivo de estagiário(a) inscrito(a) na OAB/MS.',
  SO_INSCRITO: 'Este pedido é exclusivo de quem já possui inscrição na OAB/MS.',
  JA_INSCRITO: 'Você já possui inscrição na OAB/MS, por isso este pedido não está disponível para você.'
};

/**
 * Explica por que quem está logado não pode fazer o pedido — ex: PF abrindo
 * por link direto um Distrato de Sociedade, que é feito com o login da PJ.
 * Não muda a regra de acesso: lê isPedidoVisivelParaUsuario nos 4 perfis pra
 * saber quem PODE fazer o pedido e escolhe a mensagem a partir disso.
 *
 * @param {{id: string, categoria: string|string[]}} pedido
 * @param {{tipoInscricao: 'PF'|'PJ', isInscrito: boolean, tipoCategoria?: string}} contexto
 * @returns {string|null} mensagem pra tela, ou null se o pedido é visível pra
 *   quem está logado (ou se nenhum perfil vê o pedido — aí não há o que explicar)
 */
export const getMotivoBloqueioPedido = (pedido, contexto) => {
  if (isPedidoVisivelParaUsuario(pedido, contexto)) return null;

  const vePJ = isPedidoVisivelParaUsuario(pedido, PERFIL_PJ);
  const veNaoInscrita = isPedidoVisivelParaUsuario(pedido, PERFIL_PF_NAO_INSCRITA);
  const veAdvogado = isPedidoVisivelParaUsuario(pedido, PERFIL_PF_ADVOGADO);
  const veEstagiario = isPedidoVisivelParaUsuario(pedido, PERFIL_PF_ESTAGIARIO);
  const vePF = veNaoInscrita || veAdvogado || veEstagiario;

  if (contexto.tipoInscricao === 'PJ') {
    if (!vePF) return null;
    // Registro de Sociedade, Comissões de Advogado...: só a PF Advogado(a) faz.
    return veAdvogado && !veNaoInscrita && !veEstagiario ? MOTIVOS_BLOQUEIO_PEDIDO.SO_ADVOGADO_PF : MOTIVOS_BLOQUEIO_PEDIDO.SO_PF;
  }

  // A partir daqui, tipoInscricao === 'PF'
  if (!vePF) return vePJ ? MOTIVOS_BLOQUEIO_PEDIDO.SO_PJ : null;

  // Inscrição de Estagiário, Suplementar, Transferência (e Originária pra
  // Advogado(a)): só pra quem ainda não é inscrito(a).
  if (veNaoInscrita && contexto.isInscrito) return MOTIVOS_BLOQUEIO_PEDIDO.JA_INSCRITO;

  if (veAdvogado && veEstagiario) return MOTIVOS_BLOQUEIO_PEDIDO.SO_INSCRITO;
  if (veAdvogado) return vePJ ? MOTIVOS_BLOQUEIO_PEDIDO.SO_ADVOGADO_OU_SOCIEDADE : MOTIVOS_BLOQUEIO_PEDIDO.SO_ADVOGADO;
  if (veEstagiario) return MOTIVOS_BLOQUEIO_PEDIDO.SO_ESTAGIARIO;

  return null;
};

// Pra PJ, a sociedade dos pedidos de Sociedade É quem está logado: o nome e o
// CNPJ vêm dos dados do login (já disponíveis ao abrir o pedido, sem esperar o
// /auth/profile) e ficam travados, como nome/CPF nos pedidos de PF. Pra PF
// Advogado(a) esses mesmos campos são os dados de OUTRA pessoa (a sociedade do
// pedido) e continuam livres — por isso a marcação só existe na versão PJ.
const IDENTIDADE_DA_PJ_LOGADA = {
  nomeSociedade: (usuario) => usuario?.nome,
  cnpj: (usuario) => (usuario?.documento ? formatCPFCNPJ(String(usuario.documento)) : null)
};

const nomesDosCampos = (steps) =>
  new Set(steps.flatMap(step => step.sections.flatMap(section => section.fields.map(field => field.name))));

// Campos `apenasNaoInscrito` só servem pra PF sem inscrição na OAB (ex: a qual
// pedido de inscrição uma Juntada se refere — quem é inscrito já tem a própria
// categoria). Seção que ficar vazia sai junto. Esses campos não vão pro
// DadosRequerimento, então o dadosRequerimentoConfig não muda.
const removerCamposApenasNaoInscrito = (config) => {
  const temCampo = config.steps.some(step => step.sections.some(section => section.fields.some(field => field.apenasNaoInscrito)));
  if (!temCampo) return config;

  return {
    ...config,
    steps: config.steps.map(step => ({
      ...step,
      sections: step.sections
        .map(section => ({ ...section, fields: section.fields.filter(field => !field.apenasNaoInscrito) }))
        .filter(section => section.fields.length > 0)
    }))
  };
};

/**
 * Versão do config de um pedido ajustada a quem está logado. Hoje só trata PJ:
 * - etapa com `versaoPJ` é trocada por ela (ex: Endereços da Atualização
 *   Cadastral vira só o Endereço Comercial, ver enderecoComercialStep);
 * - campos com `apenasPF: true` somem (ex: Etnia, Deficiência e AASP), e com
 *   eles as seções/etapas que ficarem vazias;
 * - Nome da Sociedade e CNPJ ganham `valorDoCadastro` com o dado do login da PJ
 *   (ver IDENTIDADE_DA_PJ_LOGADA) e `disabled` — o wizard preenche o formulário com
 *   esse valor. Se o login não trouxer o dado, o campo fica livre pra digitar;
 * - o mapeamento em DadosRequerimento de todo campo que deixou de existir sai
 *   junto (campos, endereços por CEP e municípios por nome);
 * - a descrição vira `descricaoPJ`, se o pedido declarar.
 * Pra PF devolve o config intacto, exceto pra PF já inscrita: aí somem os
 * campos `apenasNaoInscrito` (ver removerCamposApenasNaoInscrito).
 *
 * @param {Object|null} config - config.js do pedido
 * @param {'PF'|'PJ'} tipoInscricao
 * @param {Object} [usuario] - usuário logado (useAuth), fonte do nome/CNPJ da PJ
 * @param {boolean} [isInscrito] - PF inscrita na OAB (oabData.isInscrito)
 */
export const adaptarConfigAoPerfil = (config, tipoInscricao, usuario = null, isInscrito = false) => {
  if (!config) return config;
  if (tipoInscricao !== 'PJ') return isInscrito ? removerCamposApenasNaoInscrito(config) : config;

  const steps = config.steps
    .map(step => step.versaoPJ || step)
    .map(step => ({
      ...step,
      sections: step.sections
        .map(section => ({
          ...section,
          fields: section.fields
            .filter(field => !field.apenasPF)
            .map(field => {
              const valorDoCadastro = IDENTIDADE_DA_PJ_LOGADA[field.name]?.(usuario);
              return valorDoCadastro ? { ...field, valorDoCadastro, disabled: true } : field;
            })
        }))
        .filter(section => section.fields.length > 0)
    }))
    .filter(step => step.sections.length > 0);

  // Só tira do DadosRequerimento o que esta adaptação removeu — um mapeamento
  // de campo que já não estava nas etapas (se existir) continua como estava.
  const camposRestantes = nomesDosCampos(steps);
  const camposRemovidos = new Set([...nomesDosCampos(config.steps)].filter(nome => !camposRestantes.has(nome)));
  const dadosOriginal = config.dadosRequerimentoConfig;
  const dadosRequerimentoConfig = dadosOriginal && {
    ...dadosOriginal,
    campos: (dadosOriginal.campos || []).filter(c => !camposRemovidos.has(c.field)),
    enderecos: (dadosOriginal.enderecos || []).filter(e => !camposRemovidos.has(e.cepField)),
    municipiosPorNome: (dadosOriginal.municipiosPorNome || []).filter(m => !camposRemovidos.has(m.field))
  };

  return {
    ...config,
    descricao: config.descricaoPJ || config.descricao,
    steps,
    dadosRequerimentoConfig
  };
};
